import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    ArrowLeft,
    CheckCircle,
    CircleNotch,
    Warning,
} from "@phosphor-icons/react";
import { submitContact, trackEvent } from "../lib/api";

export default function Contact() {
    const [params] = useSearchParams();
    const submissionId = params.get("ref") || "";

    const [form, setForm] = useState({
        name: "",
        email: "",
        idea_one_sentence: "",
        biggest_doubt: "",
        permission_to_contact: false,
    });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const setField = (k) => (e) => {
        const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((p) => ({ ...p, [k]: v }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.name.trim() || !form.email.trim() || !form.biggest_doubt.trim()) {
            setError("Please fill name, email and your biggest question or uncertainty.");
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
            setError("Please enter a valid email address.");
            return;
        }
        if (!form.permission_to_contact) {
            setError("Please grant permission for us to contact you about your submission.");
            return;
        }
        setLoading(true);
        try {
            await submitContact({
                name: form.name.trim(),
                email: form.email.trim(),
                idea_one_sentence: form.idea_one_sentence.trim() || null,
                biggest_doubt: form.biggest_doubt.trim(),
                permission_to_contact: form.permission_to_contact,
                submission_id: submissionId || null,
            });
            trackEvent("review_request", { submissionId: submissionId || null });
            setDone(true);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(
                Array.isArray(detail)
                    ? detail.map((d) => d.msg).join(", ")
                    : detail || "Could not submit your request. Try again.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <main data-testid="contact-page" className="bg-biks-bg">
            <div className="mx-auto max-w-3xl px-6 py-20 lg:px-10 lg:py-28">
                <Link
                    to="/"
                    data-testid="contact-back-link"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-biks-text3 hover:text-white"
                >
                    <ArrowLeft size={12} weight="bold" /> Back to home
                </Link>
                <div className="section-title mt-10">Private review</div>
                <h1 className="font-display mt-5 text-4xl font-bold tracking-tight text-white sm:text-5xl">
                    Request a Private Idea &amp; Strategy Review
                </h1>
                <p className="mt-6 max-w-xl text-base text-biks-text2">
                    BoM can stress-test the structure behind an idea without
                    needing to know the idea itself. A private review takes it
                    one step further by looking at your BoM result in light of
                    the idea and the context around it.
                </p>
                <p className="mt-4 max-w-xl text-base text-biks-text2">
                    Share as much or as little of your idea as you want. I'll
                    look at where the specifics strengthen or weaken the result,
                    what BoM may not have captured and where I'd be most
                    cautious before you spend more time or money.
                </p>
                <p className="mt-4 font-mono-data text-lg text-white">
                    $120 · 60 minutes · one-on-one video call
                </p>

                {done ? (
                    <div
                        data-testid="contact-success"
                        className="mt-12 rounded-lg border border-biks-ok/40 bg-biks-ok/5 p-8"
                    >
                        <div className="flex items-center gap-3 text-biks-ok">
                            <CheckCircle size={18} weight="bold" />
                            <span className="overline !text-biks-ok">Submitted</span>
                        </div>
                        <p className="mt-4 text-sm text-white">
                            Thanks, your request has been received. I'll review
                            your BoM result and email you to arrange the session
                            and payment.
                        </p>
                        <Link
                            to="/"
                            data-testid="contact-success-home"
                            className="mt-8 inline-flex items-center gap-2 rounded-md border border-biks-line px-4 py-2 text-sm hover:border-biks-blueSoft"
                        >
                            Back to BoM
                        </Link>
                    </div>
                ) : (
                    <form
                        data-testid="contact-form"
                        onSubmit={onSubmit}
                        noValidate
                        className="mt-12 grid gap-6 rounded-xl border border-biks-line/70 bg-biks-surface/40 p-8 lg:p-10"
                    >
                        <Field label="Name" name="contact-name" value={form.name} onChange={setField("name")} placeholder="Jane Doe" />
                        <Field
                            label="Email"
                            name="contact-email"
                            type="text"
                            value={form.email}
                            onChange={setField("email")}
                            placeholder="jane@company.com"
                        />
                        <Field
                            label={<><span className="normal-case">BoM</span> result ID</>}
                            name="contact-scan-id"
                            value={submissionId}
                            onChange={() => {}}
                            placeholder="Auto-filled when you arrive from a result page"
                            readOnly
                        />
                        <Field
                            label="Optional: one-sentence description of the idea"
                            name="contact-idea"
                            value={form.idea_one_sentence}
                            onChange={setField("idea_one_sentence")}
                            placeholder="A tool that helps X do Y by Z."
                        />
                        <div>
                            <label htmlFor="contact-doubt-input" className="overline mb-3 block">
                                Biggest question or uncertainty
                            </label>
                            <textarea
                                id="contact-doubt-input"
                                data-testid="contact-doubt-input"
                                rows={6}
                                value={form.biggest_doubt}
                                onChange={setField("biggest_doubt")}
                                placeholder="Name the single thing that, if it goes wrong, will sink the idea."
                                className="w-full resize-none rounded-md border border-biks-line bg-biks-bg p-4 text-sm text-white placeholder:text-biks-text3 focus:border-biks-blue focus:outline-none"
                            />
                        </div>

                        <label className="flex items-start gap-3 text-sm text-biks-text2" htmlFor="contact-permission-input">
                            <input
                                id="contact-permission-input"
                                data-testid="contact-permission-input"
                                type="checkbox"
                                checked={form.permission_to_contact}
                                onChange={setField("permission_to_contact")}
                                className="mt-1 h-4 w-4 rounded border-biks-line bg-biks-bg accent-biks-blue"
                            />
                            <span>
                                I give BoM permission to contact me about my submission.
                            </span>
                        </label>

                        {error && (
                            <div
                                data-testid="contact-error"
                                className="flex items-start gap-2 rounded-md border border-biks-danger/60 bg-biks-danger/5 p-3 text-sm text-biks-danger"
                            >
                                <Warning size={14} weight="bold" className="mt-0.5" />
                                <span>{error}</span>
                            </div>
                        )}

                        <button
                            type="submit"
                            data-testid="contact-submit-button"
                            disabled={loading}
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-biks-blue px-6 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:bg-blue-500 disabled:opacity-50"
                        >
                            {loading ? (
                                <>
                                    <CircleNotch size={14} weight="bold" className="animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                "Send request"
                            )}
                        </button>
                    </form>
                )}
            </div>
        </main>
    );
}

function Field({ label, name, type = "text", value, onChange, placeholder, readOnly = false }) {
    return (
        <div>
            <label htmlFor={`${name}-input`} className="overline mb-3 block">
                {label}
            </label>
            <input
                id={`${name}-input`}
                data-testid={`${name}-input`}
                type={type}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                readOnly={readOnly}
                className={`w-full rounded-md border border-biks-line bg-biks-bg p-4 text-sm text-white placeholder:text-biks-text3 focus:border-biks-blue focus:outline-none ${readOnly ? "opacity-70" : ""}`}
            />
        </div>
    );
}
